import { EMMETT } from "./collection";
import type { UiCopy } from "./i18n";
import { enqueue, whenReady } from "./mount-queue";
import { sceneFor } from "./splat/scenes";
import { DEFAULT_REGION, type RegionId } from "./regions";
import { scenesIn } from "./splat/scenes";

/**
 * Rooms that were scanned rather than built.
 *
 * The purpose-built exhibitions are modelled from a plan; these are photographs of real
 * space, reconstructed as Gaussian splats and walkable in the browser. They lead the
 * index because they answer the question a client actually asks — "can you put *our*
 * building in this?" — in a way a rendered room cannot.
 *
 * Two deliberate constraints:
 *
 *   Nothing loads until asked. Each of these is a WebGL context and a large download.
 *   Mounting both on arrival would cost every visitor the price of two 3D scenes before
 *   they had chosen to look at either, and on kiosk hardware that is the whole budget.
 *
 *   The frame is third-party. It is sandboxed to what a viewer needs and nothing more,
 *   and it is not given permission to reach back into this page.
 */

export type ScannedRoom = {
  /** Our own scene slug, or a SuperSplat scene id. The URL is derived, never pasted. */
  id: string;
  title: string;
  /** Who captured it. Empty means unconfirmed, and the UI says so rather than guessing. */
  credit: string;
  /** Where the credit points. Empty when the credited party has nowhere to point to. */
  creditUrl: string;
  /**
   * True when the room was reconstructed from photographs rather than captured. It
   * changes the sentence under the card, because "generated from photographs by X" and
   * a bare name are different claims about how the work was done.
   */
  generated: boolean;
  /**
   * `own` is served from this site by our own viewer; `sample` is a stranger's scene in
   * a third-party frame. The distinction is not cosmetic — it decides what the page is
   * allowed to claim, and only samples carry the borrowed-material notice.
   */
  source: "own" | "sample";
};

/**
 * Borrowed demonstrations, kept below and clearly labelled as somebody else's.
 *
 * Empty, and deliberately still here. The two SuperSplat scenes that used to fill it
 * were somebody's untitled uploads: we could not name who made them, so the cards said
 * "Untitled scan" and pointed at a stranger's viewer for the credit. That was honest but
 * it was not something to show a client — an unattributed scan sitting in a section
 * whose argument is that everything on this site is accounted for. They are gone.
 *
 * The list stays because the distinction it draws is still wanted: our own studio
 * embeds are meant to land here, and they will need the same borrowed-material notice
 * and the same sandbox that only a `sample` row switches on.
 */
export const SAMPLE_ROOMS: readonly ScannedRoom[] = [];

/**
 * The rooms belonging to one heritage.
 *
 * The borrowed SuperSplat demonstrations are attached only to the default region. They
 * exist to show the format where we had nothing of our own; Greece has material that is
 * entirely traceable to one photographer, and mixing a stranger's unattributed scene
 * into it would put a hole in a section whose whole point is that every piece is
 * accounted for.
 */
export function roomsFor(region: RegionId): readonly ScannedRoom[] {
  const own: ScannedRoom[] = scenesIn(region).map((scene) => ({
    id: scene.slug,
    title: scene.title,
    credit: scene.origin === "generated" ? EMMETT.name : "",
    creditUrl: scene.origin === "generated" ? EMMETT.url : "",
    generated: scene.origin === "generated",
    source: "own" as const,
  }));
  return region === DEFAULT_REGION ? [...own, ...SAMPLE_ROOMS] : own;
}

export const SCANNED_ROOMS: readonly ScannedRoom[] = roomsFor(DEFAULT_REGION);

export function viewerUrl(room: Pick<ScannedRoom, "id" | "source">): string {
  return room.source === "own"
    ? `splat.html?scene=${encodeURIComponent(room.id)}`
    : `https://superspl.at/s?id=${encodeURIComponent(room.id)}`;
}

export function createShowcase(copy: UiCopy, rooms: readonly ScannedRoom[] = SCANNED_ROOMS): HTMLElement {
  const section = document.createElement("section");
  section.className = "showcase";
  section.dataset.reveal = "";

  const eyebrow = document.createElement("p");
  eyebrow.className = "eyebrow";
  eyebrow.textContent = copy.showcaseEyebrow;

  // A room generated from photographs is not a scan of anywhere, and the heading and
  // the intro are both claims about how it was made. Saying "scanned" and "captured"
  // over the Greek rooms is the same error the eyebrow used to make, one level up.
  const generated = rooms.length > 0 && rooms.every((room) => room.generated);

  const heading = document.createElement("h2");
  heading.className = "showcase__title";
  heading.textContent = generated ? copy.showcaseTitleGenerated : copy.showcaseTitle;

  const intro = document.createElement("p");
  intro.className = "showcase__intro";
  intro.textContent = generated ? copy.showcaseIntroGenerated : copy.showcaseIntro;

  // Said once and said plainly. A client reading this page has to be able to tell
  // borrowed demonstration material from what they would actually be buying, and a
  // caption tucked under a card is not where that lands.
  const notice = document.createElement("p");
  notice.className = "showcase__notice";
  notice.textContent = copy.showcaseSampleNotice;
  // Only if there is borrowed material to caveat. Applying it to our own scans would
  // disclaim work that is ours, which is the opposite error and just as misleading.
  notice.hidden = !rooms.some((room) => room.source === "sample");

  const list = document.createElement("div");
  list.className = "showcase__list";
  // Every room opens itself as it is reached. Capping this to the first one kept the
  // download honest but left four cards looking like photographs of somewhere, which is
  // exactly the impression this section exists to break. The cost is controlled by when
  // rather than by how many: nothing loads until it is a viewport away, and a room that
  // has been scrolled well past gives its WebGL context back.
  for (const room of rooms) list.append(createRoom(room, copy));

  section.append(eyebrow, heading, intro, notice, list);
  return section;
}

function createRoom(room: ScannedRoom, copy: UiCopy): HTMLElement {
  const card = document.createElement("article");
  card.className = "showcase__room";
  card.dataset.roomId = room.id;

  const frame = document.createElement("div");
  frame.className = "showcase__frame";

  // The still goes on the card, not only inside the viewer.
  //
  // The viewer paints its own poster, but only once the iframe has fetched a document,
  // parsed a module and run it — and until then the frame is an empty black rectangle
  // sitting directly under the last photograph. Scrolling out of the gallery into a
  // column of black boxes reads as the page having broken. Putting the same still on
  // the card means the room is never blank: the photograph is there from the first
  // paint, and the splat arrives over the top of it.
  const still = sceneFor(room.id)?.poster;
  if (still !== undefined) {
    frame.style.backgroundImage = `url(collection/${still})`;
    frame.dataset.still = "true";
  }

  // A room the visitor has scrolled to should already be running when they arrive —
  // pressing a button to start looking at something they are looking at is a step that
  // buys nothing. It still does not load on arrival at the top of the page: two WebGL
  // contexts and fifty megabytes before anyone has scrolled is the whole kiosk budget
  // spent on rooms they may never reach. Sighted at the edge of the viewport is the
  // point where it is both free and ready.
  const button = document.createElement("button");
  button.type = "button";
  button.className = "showcase__open";
  button.dataset.showcaseOpen = "";
  button.textContent = copy.showcaseOpen;
  // The room's own name, so a screen reader hears which one this opens rather than
  // hearing "Step inside" twice with nothing to tell them apart.
  button.setAttribute("aria-label", `${copy.showcaseOpen} — ${room.title}`);
  button.addEventListener("click", () => {
    mount(frame, room, copy);
    button.remove();
  });
  frame.append(button);
  // Borrowed SuperSplat embeds are not ours to auto-start: they are a third-party page
  // on a third-party host, they are the slowest thing on this index, and they exist to
  // demonstrate a format rather than to be walked through. They open on a press.
  if (room.source === "own") {
    watch(
      frame,
      () => {
        mount(frame, room, copy);
        button.remove();
      },
      () => unmount(frame, button),
    );
  }

  const caption = document.createElement("div");
  caption.className = "showcase__caption";
  const title = document.createElement("h3");
  title.textContent = room.title;
  const credit = document.createElement("p");
  credit.className = "showcase__credit";
  if (room.source === "own" && room.generated) {
    // Says what actually happened, and names the person it happened to. A room
    // reconstructed from someone's photographs is their work at one remove, and the
    // sentence has to carry both facts or it is quietly taking credit.
    const link = document.createElement("a");
    link.href = room.creditUrl;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    link.textContent = room.credit;
    credit.append(`${copy.showcaseRoomsGenerated} `, link);
  } else if (room.source === "own") {
    // Just the studio name, with no claim about how the scene came to exist. Whether
    // these were captured or generated has never been confirmed, and the card must not
    // decide it either way.
    credit.textContent = room.credit === "" ? copy.showcaseOwnCredit : room.credit;
  } else if (room.credit === "") {
    // We cannot read the author from here — the viewer page carries no metadata until
    // its own scripts run — but SuperSplat names them inside the scene. Pointing at the
    // source is a truthful credit; typing a guess under someone's work is not.
    const link = document.createElement("a");
    link.href = viewerUrl(room);
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    link.textContent = copy.showcaseCreditLink;
    credit.append(link);
  } else {
    credit.textContent = room.credit;
  }
  caption.append(title, credit);

  card.append(frame, caption);
  return card;
}

/**
 * Mount as the frame comes into view, and never again.
 *
 * Where IntersectionObserver is missing the button stays and the room opens on press,
 * which is the same feature one step slower rather than a broken card.
 */
function watch(frame: HTMLElement, open: () => void, close: () => void): void {
  if (typeof IntersectionObserver === "undefined") return;
  // Two viewports ahead. One was not enough: a .sog is twenty-odd megabytes and takes
  // several seconds to sort after it lands, so a room that only starts fetching as it
  // comes over the horizon is still assembling itself when it is being looked at.
  const near = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) if (entry.isIntersecting) open();
    },
    { rootMargin: "200% 0px" },
  );
  near.observe(frame);

  // And three viewports behind, it gives the context back. A browser holds only so many
  // WebGL contexts before it starts killing the oldest, which shows up as rooms higher
  // up the page silently going black; five rooms plus five objects is well over any
  // sensible budget. Far enough away that scrolling back a little does not reload.
  const far = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) if (!entry.isIntersecting) close();
    },
    { rootMargin: "300% 0px" },
  );
  far.observe(frame);
}

/** Give the context and the memory back, and put the button where it was. */
function unmount(frame: HTMLElement, button: HTMLElement): void {
  const iframe = frame.querySelector("iframe");
  if (iframe === null) return;
  iframe.remove();
  frame.querySelector(".showcase__status")?.remove();
  delete frame.dataset.waiting;
  // Hand the turn back before anything else, or every room below this one waits on a
  // viewer that has just been deleted.
  releases.get(frame)?.();
  frame.append(button);
}

/**
 * How to give up a frame's turn in the queue.
 *
 * A room that is scrolled far past while it is still loading is torn down, and if that
 * happened without releasing its turn the queue stalled until the twenty-second
 * failsafe — with every room below it waiting on a viewer that no longer exists.
 */
const releases = new WeakMap<HTMLElement, () => void>();

function mount(frame: HTMLElement, room: ScannedRoom, copy: UiCopy): void {
  if (frame.querySelector("iframe") !== null) return;

  const status = document.createElement("p");
  status.className = "showcase__status";
  status.setAttribute("role", "status");
  status.textContent = copy.showcaseLoading;
  // Said as soon as the room is spoken for, not when its turn comes. A frame sitting
  // silently in the queue is indistinguishable from a frame that has failed.
  frame.append(status);
  frame.dataset.waiting = "true";

  enqueue((done) => {
    // Still wanted? `waiting` is cleared by `unmount`, so a room the visitor scrolled
    // far past while it sat in the queue gives up its turn instead of downloading
    // twenty megabytes for a frame nobody is looking at any more. Deliberately not
    // `isConnected`: a fragment that has not been appended yet is not the same thing
    // as one that has been thrown away.
    if (frame.dataset.waiting !== "true") {
      done();
      return;
    }
    build(frame, room, status, done);
  });
}

function build(
  frame: HTMLElement,
  room: ScannedRoom,
  status: HTMLElement,
  done: () => void,
): void {
  const iframe = document.createElement("iframe");
  iframe.src = viewerUrl(room);
  iframe.title = room.title;
  iframe.className = "showcase__viewer";
  iframe.setAttribute("loading", "lazy");
  // What a splat viewer needs: a full-screen button and headset pose. Nothing else —
  // no camera, microphone, geolocation or payment travels to a third-party frame.
  iframe.setAttribute("allow", "fullscreen; xr-spatial-tracking");
  // Scripts and same-origin so its own WebGL and storage work; no allow-top-navigation,
  // so the embed cannot navigate the museum out from under the visitor.
  if (room.source === "sample") {
    iframe.setAttribute("sandbox", "allow-scripts allow-same-origin allow-popups");
    iframe.setAttribute("referrerpolicy", "no-referrer");
  }
  // Stood down as soon as the viewer's own document exists, not when its splat has
  // finished arriving. The two are seconds apart, and for those seconds the card said
  // "Opening the 3D view…" directly on top of the viewer saying "Loading the room…" —
  // two lines in one place, meaning the same thing, one legible through the other.
  // The viewer is the better voice for that wait anyway: it is the thing that knows.
  iframe.addEventListener("load", () => {
    status.remove();
  });
  // The queue still turns on the viewer actually being up. A document that has loaded
  // has not yet compiled a shader or sorted a splat, and releasing the next room then
  // is how three of them end up doing the expensive part at once.
  const stop = whenReady(iframe, () => {
    status.remove();
    delete frame.dataset.waiting;
    releases.delete(frame);
    done();
  });
  const give = (): void => {
    stop();
    releases.delete(frame);
    done();
  };
  releases.set(frame, give);
  iframe.addEventListener("error", give);

  frame.append(iframe);
}
